import {computed, makeObservable, observable} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import {BaseModel} from '../Base/Base.model';
import {CurrencyModel} from '../Currency/Currency.model';

const ModelName = 'PriceModel';

export class PriceModel extends BaseModel {
  constructor(
    public value: number,
    public currency: CurrencyModel = new CurrencyModel('usd'),
  ) {
    super({
      id: '',
    });

    makeObservable<PriceModel, 'formattedPrice'>(this, {
      currency: observable,
      currencyString: computed,
      formattedPrice: computed,
      formattedString: computed,
      paidOrFreeString: computed,
      value: observable,
    });
  }

  get currencyString() {
    return `${this.currency.sign}${this.formattedPrice}`;
  }

  get formattedString() {
    return this.formattedPrice;
  }

  static override get modelName(): typeof ModelName {
    return ModelName;
  }

  get modelName(): typeof ModelName {
    return ModelName;
  }

  get paidOrFreeString() {
    return this.value === 0 ? i18n.t('shared:free') : i18n.t('shared:paid');
  }

  extractCommission(config = {fixed: 30, percent: 2.9}) {
    const result: PriceModel = new PriceModel(0, this.currency);

    if (this.value === 0) {
      return result;
    }

    result.value =
      Math.round(this.value * (config.percent / 100)) + config.fixed;

    return result;
  }

  private get formattedPrice() {
    const normalizedPrice = this.value / 100;

    const isWithoutFractionalPart = normalizedPrice % 1 === 0;

    return normalizedPrice.toLocaleString(
      // takes a device locale
      undefined,
      {
        minimumFractionDigits: isWithoutFractionalPart ? 0 : 2,
      },
    );
  }
}

export namespace PriceModel {
  export type Props = BaseModel.ModelToProps<PriceModel>;
}
